import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet-draw";
import * as turf from "@turf/turf";

const SQ_M_TO_SQ_FT = 10.7639;

export default function RoofMap({ center, onAreaChange, onReady }) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const drawnItemsRef = useRef(null);
  const drawHandlerRef = useRef(null);

  useEffect(() => {
    if (mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: center || [29.7604, -95.3698],
      zoom: center ? 20 : 11,
      zoomControl: true,
      maxZoom: 22,
    });
    mapRef.current = map;

    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 22, maxNativeZoom: 19, attribution: "Imagery © Esri" }
    ).addTo(map);

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    drawnItemsRef.current = drawnItems;

    const recompute = () => {
      let totalSqM = 0;
      drawnItems.eachLayer((layer) => {
        const gj = layer.toGeoJSON();
        totalSqM += turf.area(gj);
      });
      onAreaChange(Math.round(totalSqM * SQ_M_TO_SQ_FT));
    };

    map.on(L.Draw.Event.CREATED, (e) => {
      e.layer.setStyle?.({ color: "#EF233C", weight: 2, fillColor: "#EF233C", fillOpacity: 0.25 });
      drawnItems.addLayer(e.layer);
      recompute();
    });
    map.on(L.Draw.Event.EDITED, recompute);
    map.on(L.Draw.Event.DELETED, recompute);

    if (onReady) {
      onReady({
        startDraw: () => {
          drawHandlerRef.current = new L.Draw.Polygon(map, {
            shapeOptions: { color: "#EF233C", weight: 2, fillColor: "#EF233C", fillOpacity: 0.25 },
            allowIntersection: false,
            showArea: false,
          });
          drawHandlerRef.current.enable();
        },
        clearAll: () => {
          drawnItems.clearLayers();
          onAreaChange(0);
        },
        undo: () => {
          const layers = drawnItems.getLayers();
          if (layers.length) {
            drawnItems.removeLayer(layers[layers.length - 1]);
            recompute();
          }
        },
      });
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (mapRef.current && center) {
      mapRef.current.flyTo(center, 20, { duration: 1.2 });
    }
  }, [center]);

  return <div ref={containerRef} data-testid="roof-map" className="w-full h-full" />;
}
